import qs from 'qs';
import axios from 'axios';

import i18next from 'i18next';
import request from 'superagent';
import { fileToBase64 } from 'utils/FilesUtils';
import { ACTION } from 'constants/LaneRates';
import client from './rest/AdminClient';
import AdminServerActionCreators from '../actions/AdminServerActionCreators';
import ShipmentsActionCreators from '../actions/ShipmentsActionCreators';
import { getBearerGrantTypeAuthHeaders } from '../helpers/auth_helpers';
import { isCSV } from './documents';
import { showToast } from 'stores/ToastStore';
import lodash from 'lodash';
import { createCSVDownloadLink } from './csv';
import queryClient from './queryClient';

export const useAccessorialRequests = (query = {}, options = {}) => {
  const defaultQuery = { type: 'accessorial' };
  const queryString = qs.stringify({ ...defaultQuery, ...query });
  const url = Object.keys(query).length
    ? `${API_HOST}/api/accessorials?${queryString}`
    : `${API_HOST}/api/accessorials`;

  return queryClient.fetchQuery(
    ['accessorialRequests', query],
    async () => {
      const opts = {
        method: 'get',
        url,
        headers: {
          'Content-Type': 'application/json',
          Authorization: getBearerGrantTypeAuthHeaders(),
        },
      };
      if (options.cancelToken) {
        opts.cancelToken = options.cancelToken;
      }
      const response = await axios(opts);
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
      ...options,
    },
  );
};
export async function getRateConditionTypes() {
  try {
    const url = `${PRICING_SERVICE_HOST}/rates/conditions/types`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': PRICING_SERVICE_API_KEY,
      },
    };
    const res = await axios.get(url, options);

    return (
      res?.data?.conditionTypes.map((ct) => {
        ct.typeId = ct.id;
        return lodash.pick(ct, ['typeId', 'type']);
      }) || []
    );
  } catch (e) {
    console.log('ERROR_FETCHING_RATE_CONDITION_TYPES');
    throw e;
  }
}

export function updateRateSequence({ laneId, customer, laneRates = [] }) {
  const url = `${PRICING_SERVICE_HOST}/lanes/lanerates/resequence`;
  const options = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios.post(url, { laneId, customer, laneRates }, options);
}

export const getUserLaneRates = (query = '') => {
  return client({
    method: 'GET',
    path: `/api/rates/user/lanes?${query}`,
  });
};

export const getUserLaneRatesV3 = (query = '') => {
  const url = `${PRICING_SERVICE_HOST}/v3/lanes/rates?${query}`;
  const options = {
    method: 'get',
    url,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios(options);
};

export const getUserLaneRatesV2 = (query = '') => {
  const url = `${PRICING_SERVICE_HOST}/lanes/user/v2/rates?${query}`;
  const options = {
    method: 'get',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios(options);
};

export const exportUserLaneRates = (query = '') => {
  return axios.get(
    `${PRICING_SERVICE_HOST}/lanes/user/v2/rates/export-to-file?${query}`,
    { responseType: 'blob', headers: { 'x-api-key': PRICING_SERVICE_API_KEY } },
  );
};

export const exportUserLaneRatesWithConditions = async (query = '') => {
  const url = `${PRICING_SERVICE_HOST}/v3/lanes/rates/export-to-file?${query}`;
  return axios.get(url, {
    responseType: 'blob',
    headers: { 'x-api-key': PRICING_SERVICE_API_KEY },
  });
};

export const getLaneAccessorialRates = (query = '') => {
  return client({
    method: 'GET',
    path: `/api/rates/user/accessorial/lanes?${query}`,
  });
};

export const getLaneAccessorialRatesV2 = (query = '') => {
  const url = `${PRICING_SERVICE_HOST}/lanes/accessorial/v2/rates?${query}`;
  const options = {
    method: 'get',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios(options);
};

export const getUserLaneRateAudits = (query = '') => {
  return client({
    method: 'GET',
    path: `/api/rates/user/lanes/audits?${query}`,
  });
};

export const getLaneAccessorialRateAudits = (query = '') => {
  return client({
    method: 'GET',
    path: `/api/rates/user/accessorial/lanes/audits?${query}`,
  });
};

export const getLaneRatesWithRateConditions = (query = '') => {
  const url = `${PRICING_SERVICE_HOST}/v3/lanes/rates/full${query}`;
  const options = {
    method: 'get',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios(options);
};

export const updateLaneRatesWithRateConditions = (data, modalType) => {
  const action = modalType === 'create' ? 'create' : 'update';
  const url = `${PRICING_SERVICE_HOST}/v3/lanes/user/rates`;
  const options = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  return axios.put(url, { laneRates: data, action }, options);
};

export const updateLaneRates = (data, modalType) => {
  const action = modalType === 'create' ? 'create' : 'update';
  data = { laneRates: data, action };
  return client({
    method: 'PUT',
    path: `/api/rates/user/lanes`,
    entity: JSON.stringify(data),
  });
};

export const updateLaneAccessorialRates = (data, action = ACTION.UPSERT) => {
  const body = { accessorialRates: data, action };
  const options = {
    method: 'PUT',
    path: '/api/rates/accessorials/lanes',
    entity: JSON.stringify(body),
  };
  return client(options);
};

export const alreadyConfirmedLaneRates = (data, files, action) => {
  data = { laneRates: data };
  return new Promise((resolve, reject) => {
    const req = request.put(`${API_HOST}/api/rates/user/lanes/confirmed`);
    req.field('laneData', JSON.stringify(data));
    req.field('action', action);
    req.field('Content-Type', 'multipart/form-data');
    req.set('Authorization', getBearerGrantTypeAuthHeaders());

    files.forEach((file) => {
      req.attach('file', file);
    });
    req.end((err, res) => {
      if (err) {
        reject(res);
      }
      if (res.body.success) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  });
};

export const alreadyConfirmedLaneRatesV3 = (data, files, action) => {
  data = { laneRates: data };
  return new Promise((resolve, reject) => {
    const req = request.put(`${API_HOST}/api/v3/rates/user/lanes/confirmed`);
    req.field('laneData', JSON.stringify(data));
    req.field('action', action);
    req.field('Content-Type', 'multipart/form-data');
    req.set('Authorization', getBearerGrantTypeAuthHeaders());

    files.forEach((file) => {
      req.attach('file', file);
    });
    req.end((err, res) => {
      if (err) {
        reject(res);
      }
      if (res.body.success) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  });
};

export const alreadyConfirmedAccessorialRates = (data, files) => {
  data = { accessorialRates: data };
  return new Promise((resolve, reject) => {
    const req = request.put(`${API_HOST}/api/rates/accessorials/confirmed`);
    req.field('accessorialData', JSON.stringify(data));
    req.field('Content-Type', 'multipart/form-data');
    req.set('Authorization', getBearerGrantTypeAuthHeaders());

    files.forEach((file) => {
      req.attach('file', file);
    });
    req.end((err, res) => {
      if (err) {
        reject(res);
      }
      if (res.body.success) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  });
};

export const laneRateEmailConfirmation = (token, status) => {
  const data = { token, status };
  return client({
    method: 'PUT',
    path: `/api/rates/user/lanes/emailConfirm`,
    entity: JSON.stringify(data),
  });
};

export const laneRateAccessorialEmailConfirmation = (token, status) => {
  const data = { token, status };
  return client({
    method: 'PUT',
    path: `/api/rates/lane/accessorial/emailConfirm`,
    entity: JSON.stringify(data),
  });
};

export const getAccessorialRequestRates = (accessorials) => {
  return client({
    method: 'POST',
    path: '/api/rates/accessorials',
    entity: JSON.stringify({ accessorials }),
  });
};

export const resolveAccessorialsStatus = (body) => {
  return client({
    method: 'POST',
    path: '/api/accessorials/resolve-status',
    entity: JSON.stringify(body),
  });
};

export const createAccessorialRequest = (exception) => {
  return client({
    method: 'POST',
    path: '/api/accessorials',
    entity: JSON.stringify(exception),
  });
};

export const downloadAccessorialExceptionsCSV = (columns, query) => {
  return client({
    method: 'POST',
    path: `/api/accessorials/csv-from-query?${qs.stringify({ ...query })}`,
    entity: JSON.stringify(columns),
  });
};

export const getAccessorialRequestsAutoDeclineDates = (requestIds) => {
  return client({
    method: 'POST',
    path: `/api/v2/accessorials/auto-decline-date`,
    entity: JSON.stringify({ requests: requestIds }),
  });
};

const createBulkAccessorialRequests = (requests) => {
  return client({
    method: 'POST',
    path: '/api/accessorials/bulk',
    entity: JSON.stringify(requests),
  });
};

export const updateAccessorialException = (exception) => {
  return client({
    method: 'PATCH',
    path: '/api/accessorials',
    entity: JSON.stringify(exception),
  });
};

const updateAccessorialRequest = (updates) => {
  return client({
    method: 'PATCH',
    path: '/api/accessorials/',
    entity: JSON.stringify(updates),
  });
};

export const getAccessorialRequestById = async (id) => {
  const url = `${API_HOST}/api/accessorials/${id}`;
  const opts = {
    method: 'get',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: getBearerGrantTypeAuthHeaders(),
    },
  };
  const response = await axios(opts);
  return response?.data?.request?.item;
};

export const getAccessorialRequests = async (query = {}, options = {}) => {
  const defaultQuery = { type: 'accessorial' };
  const queryString = qs.stringify({ ...defaultQuery, ...query });
  const url = Object.keys(query).length
    ? `${API_HOST}/api/accessorials?${queryString}`
    : `${API_HOST}/api/accessorials`;

  const opts = {
    method: 'get',
    url,
    headers: {
      'Content-Type': 'application/json',
      Authorization: getBearerGrantTypeAuthHeaders(),
    },
  };
  if (options.cancelToken) {
    opts.cancelToken = options.cancelToken;
  }
  return axios(opts);
};

export const getAccessorialRequestsWithStopDetails = async (query = {}) => {
  const defaultQuery = { type: 'accessorial' };
  const path = `/api/v2/accessorials/requests/details?${qs.stringify({ ...defaultQuery, ...query })}`;
  const response = await client({
    method: 'GET',
    path,
  });
  if (response.entity.success) {
    return response.entity?.result?.items || [];
  }
  const error = response.entity.error || response.error;
  throw error;
};

export const uploadCSVCreateLaneRates = async (file) => {
  if (!isCSV(file.type)) {
    throw new Error('Invalid file type');
  }
  const base64CSV = await fileToBase64(file);
  const url = `${PRICING_SERVICE_HOST}/lanes/csv-rates`;

  return new Promise((resolve, reject) => {
    const req = request.post(url);

    req.set('x-api-key', PRICING_SERVICE_API_KEY);
    req.send({ csv: base64CSV });
    req.end((err, res) => {
      if (err) {
        reject(res);
      }
      if (res.body.success) {
        resolve(res);
      } else {
        reject(res);
      }
    });
  });
};

const Utils = {
  approveAccessorials(body) {
    resolveAccessorialsStatus(body).then(
      (response) => {
        AdminServerActionCreators.approveAccessorialsSuccess(response.entity);
        const shipmentId = response.entity.result.successes[0].job._id;
        this.getAccessorialRequests({
          shipment: shipmentId,
        });
        // Refresh shipment data to update receivables/payables
        ShipmentsActionCreators.getShipment(shipmentId, true);
      },
      (e) => {
        // Ensure error has the expected structure for ToastStore
        const error = e.message
          ? e
          : { message: e.toString() || 'Approve operation failed' };
        AdminServerActionCreators.approveAccessorialsFail(error);
      },
    );
  },
  async getUserLaneRates(query) {
    const response = await getUserLaneRates(query);
    return response ? response.entity.results : [];
  },
  async getUserLaneRatesV2(query) {
    const res = await getUserLaneRatesV2(query);
    if (res && res.data) {
      return res.data;
    }
    return [];
  },
  async exportUserLaneRates(query) {
    const response = await exportUserLaneRates(query);
    let text = await response.data.text();
    createCSVDownloadLink(text, 'lane-rates.csv');
  },
  async exportUserLaneRatesWithConditions(query) {
    const response = await exportUserLaneRatesWithConditions(query);
    let text = await response.data.text();
    createCSVDownloadLink(text, 'lane-rates.csv');
  },
  async getLaneAccessorialRates(query) {
    const response = await getLaneAccessorialRates(query);
    return response ? response.entity.results : [];
  },
  async getLaneAccessorialRatesV2(query) {
    const res = await getLaneAccessorialRatesV2(query);
    if (res && res.data) {
      return res.data;
    }
    return [];
  },
  async getUserLaneRateAudits(query) {
    const response = await getUserLaneRateAudits(query);
    return response ? response.entity.results : [];
  },
  async getLaneAccessorialRateAudits(query) {
    const response = await getLaneAccessorialRateAudits(query);
    return response ? response.entity.results : [];
  },

  async updateLaneRates(data, action) {
    const response = await updateLaneRates(data, action);
    return response ? response.entity : [];
  },

  async updateLaneAccessorialRates(data, action) {
    const response = await updateLaneAccessorialRates(data, action);
    return response ? response.entity : [];
  },

  async alreadyConfirmedLaneRates(data, files, action) {
    const response = await alreadyConfirmedLaneRates(data, files, action);
    return response ? response.body : {};
  },

  async alreadyConfirmedAccessorialRates(data, files) {
    const response = await alreadyConfirmedAccessorialRates(data, files);
    return response ? response.body : {};
  },

  async laneRateAccessorialEmailConfirmation(data, status) {
    const response = await laneRateAccessorialEmailConfirmation(data, status);
    return response ? response.entity.results : {};
  },

  async laneRateEmailConfirmation(data, status) {
    const response = await laneRateEmailConfirmation(data, status);
    return response ? response.entity.results : {};
  },

  declineAccessorials(body) {
    resolveAccessorialsStatus(body).then(
      (response) => {
        AdminServerActionCreators.declineAccessorialsSuccess(response.entity);
        const shipmentId = response.entity?.result?.successes?.[0]?.job?._id;
        if (shipmentId) {
          this.getAccessorialRequests({
            shipment: shipmentId,
          });
          ShipmentsActionCreators.getShipment(shipmentId, true);
        }
      },
      (e) => {
        // Ensure error has the expected structure for ToastStore
        const error = e.message
          ? e
          : { message: e.toString() || 'Decline operation failed' };
        AdminServerActionCreators.declineAccessorialsFail(error);
      },
    );
  },
  acceptAccessorials(body) {
    resolveAccessorialsStatus(body).then(
      (response) =>
        AdminServerActionCreators.acceptAccessorialsSuccess(response.entity),
      (e) => AdminServerActionCreators.acceptAccessorialsFail(e),
    );
  },
  getAccessorialRequests(query, options) {
    const isPaginatedQuery = query.paginate;
    getAccessorialRequests(query, options).then(
      (response) => {
        if (isPaginatedQuery) {
          AdminServerActionCreators.onGetAccessorialPaginatedRequestsSuccess(
            response.data,
          );
        } else {
          AdminServerActionCreators.onGetAccessorialRequestsSuccess(
            response.data,
          );
        }
      },
      (err) => {
        if (!axios.isCancel(err)) {
          showToast(
            i18next.t('RECEIVE_ACCESSORIAL_REQUEST_FAIL'),
            i18next.t('RECEIVE_ACCESSORIAL_REQUEST_FAIL'),
            'error',
          );
        }
        if (isPaginatedQuery) {
          AdminServerActionCreators.onGetAccessorialPaginatedRequestsFail(err);
        } else {
          AdminServerActionCreators.onGetAccessorialRequestsFail(err);
        }
      },
    );
  },
  updateAccessorialRequest(updates) {
    updateAccessorialRequest(updates).then(
      (response) =>
        AdminServerActionCreators.onUpdateAccessorialRequestSuccess(response),
      (err) => AdminServerActionCreators.onUpdateAccessorialRequestFail(err),
    );
  },
  createAccessorialRequest(exception) {
    createAccessorialRequest(exception).then(
      (res) => {
        if (res.entity.success) {
          AdminServerActionCreators.createAccessorialRequestSuccess(
            res.entity.request,
          );
        } else {
          AdminServerActionCreators.createAccessorialRequestFail({
            error: 'Unknown failure in createAccessorialRequest',
          });
        }
      },
      (err) => AdminServerActionCreators.createAccessorialRequestFail(err),
    );
  },
  updateAccessorialException(exception) {
    return updateAccessorialException(exception)
      .then((res) => {
        if (res.entity.success) {
          AdminServerActionCreators.updateAccessorialExceptionSuccess(
            res.entity.results.successes,
          );
        } else {
          AdminServerActionCreators.updateAccessorialExceptionFail(res.entity);
        }
      })
      .catch((err) =>
        AdminServerActionCreators.updateAccessorialExceptionFail(err),
      );
  },

  createBulkAccessorialRequests(requests) {
    createBulkAccessorialRequests(requests)
      .then((res) =>
        AdminServerActionCreators.bulkCreateAccessorialRequestSuccess(
          res.entity.result,
        ),
      )
      .catch((err) =>
        AdminServerActionCreators.bulkCreateAccessorialRequestFail(err),
      );
  },
};

export default Utils;
